import React, { useState } from "react";
import AuthorityAssignmentField from "../shared/AuthorityAssignmentField";
import { CoverImage } from "../shared/CoverImage";
import { TextStyleControls } from "../shared/TextStyleControls";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DOW_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ServiceForm — modal body used by ServicesPage to add / edit a service.
// Form parity with Events: time, capacity, description, recurrence (7 types
// + custom interval + end conditions), authority assignment, and color.
export default function ServiceForm({ svc, prefillDate, users, canManageAuthority, isSuperAdmin = false, onSave, onClose }) {
  const prefillDow = (() => {
    if (!prefillDate) return 0;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(prefillDate);
    if (!m) return 0;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getDay();
  })();

  const initial = svc || {
    name: "",
    type: prefillDate ? "one-time" : "recurring",
    dayOfWeek: prefillDow,
    eventDate: prefillDate || "",
    time: "",
    capacity: "",
    description: "",
    color: "",
    authorityIds: [],
    recurrence: { type: "none", daysOfWeek: [], interval: 1, end: "never", until: "", count: "" },
  };
  if (!initial.recurrence) initial.recurrence = { type: "none", daysOfWeek: [], interval: 1, end: "never", until: "", count: "" };

  const [form, setForm] = useState(initial);
  const [editScopeAsk, setEditScopeAsk] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setRec = (k, v) => setForm(f => ({ ...f, recurrence: { ...(f.recurrence || {}), [k]: v } }));
  const toggleDow = (idx) => {
    const cur = Array.isArray(form.recurrence?.daysOfWeek) ? form.recurrence.daysOfWeek : [];
    const next = cur.includes(idx) ? cur.filter(x => x !== idx) : [...cur, idx].sort();
    setRec("daysOfWeek", next);
  };

  const isRecurringEdit = svc && svc.recurrence && svc.recurrence.type && svc.recurrence.type !== "none";

  const handleSave = () => {
    if (!form.name) return;
    if (isRecurringEdit) { setEditScopeAsk(true); return; }
    onSave(form);
  };

  const applyEditScope = (scope) => {
    if (scope === "occurrence") {
      const original = svc;
      const ymd = String(original.eventDate || "").slice(0, 10);
      const exceptions = Array.isArray(original.recurrence?.exceptions) ? [...original.recurrence.exceptions] : [];
      if (ymd && !exceptions.includes(ymd)) exceptions.push(ymd);
      const updatedOriginal = { ...original, recurrence: { ...(original.recurrence || {}), exceptions } };
      onSave(updatedOriginal);
      const newOne = { ...form, id: undefined, type: "one-time", recurrence: { type: "none", daysOfWeek: [], interval: 1, end: "never", until: "", count: "" } };
      onSave(newOne);
    } else {
      onSave(form);
    }
    setEditScopeAsk(false);
  };

  const recType = form.recurrence?.type || "none";

  return (
    <>
      <div className="modal-body">
        <div className="form-group mb-14">
          <label>Cover Image</label>
          <CoverImage
            value={form.coverImage || ""}
            onChange={(v) => set("coverImage", v || "")}
            editable={isSuperAdmin}
            title={form.name || "Service"}
            iconName="church"
            overlay={typeof form.overlay === "number" ? form.overlay : 55}
            textColor={form.textColor || "white"}
            textEffect={form.textEffect || "shadow"}
            data-testid="service-cover-image"
          />
          <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
            JPG, PNG, or WEBP up to 5 MB. Displays at 16:9 above the service card.
          </div>
          {isSuperAdmin && (
            <TextStyleControls
              textColor={form.textColor || "white"}
              textEffect={form.textEffect || "shadow"}
              overlay={typeof form.overlay === "number" ? form.overlay : 55}
              onColor={(v) => set("textColor", v)}
              onEffect={(v) => set("textEffect", v)}
              onOverlay={(v) => set("overlay", v)}
              previewImage={form.coverImage || ""}
              previewTitle={form.name || "Service"}
              previewSubtitle={form.time || "Sunday · Morning"}
            />
          )}
        </div>
        <div className="form-group mb-14">
          <label>Name *</label>
          <input data-testid="service-name" value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Sunday Morning Service" />
        </div>
        <div className="form-grid mb-14">
          <div className="form-group">
            <label>Type</label>
            <select data-testid="service-type" value={form.type} onChange={e => set("type", e.target.value)}>
              <option value="recurring">Recurring</option>
              <option value="one-time">One-time</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>Time</label>
            <input data-testid="service-time" type="time" value={form.time || ""} onChange={e => set("time", e.target.value)} />
          </div>
        </div>
        {form.type === "recurring" ? (
          <div className="form-group mb-14">
            <label>Day of Week</label>
            <select data-testid="service-day-of-week" value={form.dayOfWeek} onChange={e => set("dayOfWeek", parseInt(e.target.value))}>
              {DAYS.map((d, i) => <option key={`dow-${d}`} value={i}>{d}</option>)}
            </select>
          </div>
        ) : (
          <div className="form-group mb-14">
            <label>Event Date</label>
            <input data-testid="service-event-date" type="date" value={form.eventDate || ""} onChange={e => set("eventDate", e.target.value)} />
          </div>
        )}
        <div className="form-grid mb-14">
          <div className="form-group">
            <label>Capacity</label>
            <input data-testid="service-capacity" type="number" value={form.capacity || ""} onChange={e => set("capacity", e.target.value)} placeholder="e.g. 200" />
          </div>
          <ColorPickerField label="Color" testId="service-color" value={form.color} onChange={v => set("color", v)} />
        </div>
        <div className="form-group mb-14">
          <label>Description</label>
          <textarea data-testid="service-description" value={form.description || ""} onChange={e => set("description", e.target.value)} placeholder="Short description (optional)" />
        </div>

        {/* Recurrence */}
        <div className="form-group mb-14">
          <label>Recurrence</label>
          <select data-testid="service-recurrence-type" value={recType} onChange={e => setRec("type", e.target.value)}>
            <option value="none">Does not repeat</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly (select days)</option>
            <option value="biweekly">Bi-weekly</option>
            <option value="monthly-date">Monthly on the same date</option>
            <option value="monthly-weekday">Monthly on the same weekday</option>
            <option value="yearly">Yearly</option>
            <option value="custom">Custom interval</option>
          </select>
        </div>
        {recType === "weekly" && (
          <div className="form-group mb-14" data-testid="service-recurrence-days">
            <label>Repeat on</label>
            <div className="flex gap-8 flex-wrap">
              {DOW_NAMES.map((n, i) => {
                const selected = (form.recurrence?.daysOfWeek || []).includes(i);
                return (
                  <button
                    key={n}
                    type="button"
                    data-testid={`service-recurrence-dow-${i}`}
                    className={`cal-dow-chip ${selected ? "is-selected" : ""}`}
                    onClick={() => toggleDow(i)}
                  >{n}</button>
                );
              })}
            </div>
          </div>
        )}
        {recType === "custom" && (
          <div className="form-grid mb-14">
            <div className="form-group">
              <label>Every</label>
              <input data-testid="service-recurrence-interval" type="number" min={1} value={form.recurrence?.interval || 1} onChange={e => setRec("interval", parseInt(e.target.value) || 1)} />
            </div>
            <div className="form-group">
              <label>Unit</label>
              <select data-testid="service-recurrence-unit" value={form.recurrence?.unit || "weeks"} onChange={e => setRec("unit", e.target.value)}>
                <option value="days">Day(s)</option>
                <option value="weeks">Week(s)</option>
                <option value="months">Month(s)</option>
                <option value="years">Year(s)</option>
              </select>
            </div>
          </div>
        )}
        {recType !== "none" && (
          <div className="form-group mb-14">
            <label>End condition</label>
            <select data-testid="service-recurrence-end" value={form.recurrence?.end || "never"} onChange={e => setRec("end", e.target.value)}>
              <option value="never">No end date</option>
              <option value="count">End after N occurrences</option>
              <option value="until">End by date</option>
            </select>
          </div>
        )}
        {recType !== "none" && form.recurrence?.end === "count" && (
          <div className="form-group mb-14">
            <label>Number of occurrences</label>
            <input data-testid="service-recurrence-count" type="number" min={1} value={form.recurrence?.count || ""} onChange={e => setRec("count", e.target.value)} placeholder="e.g. 10" />
          </div>
        )}
        {recType !== "none" && form.recurrence?.end === "until" && (
          <div className="form-group mb-14">
            <label>End by date</label>
            <input data-testid="service-recurrence-until" type="date" value={form.recurrence?.until || ""} onChange={e => setRec("until", e.target.value)} />
          </div>
        )}

        <AuthorityAssignmentField
          value={form.authorityIds}
          onChange={v => set("authorityIds", v)}
          users={users}
          canEdit={!!canManageAuthority}
          testIdPrefix="service-authority"
        />
      </div>
      <div className="modal-footer">
        <button className="btn btn-outline" data-testid="service-cancel" onClick={onClose}>Cancel</button>
        <button className="btn btn-gold" data-testid="service-save" onClick={handleSave}>Save</button>
      </div>

      {editScopeAsk && (
        <div className="modal-overlay" data-testid="service-edit-scope-modal" onClick={() => setEditScopeAsk(false)}>
          <div className="modal-card cal-modal-scrollable" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="cal-day-modal-header">
              <div className="cal-day-modal-title">Edit recurring service</div>
              <button type="button" className="cal-day-modal-close" data-testid="service-edit-scope-close" onClick={() => setEditScopeAsk(false)}>×</button>
            </div>
            <div className="cal-day-modal-body">
              <p style={{ fontSize: 13, color: "var(--kc-sub)", marginBottom: 12 }}>This service repeats. Apply your changes to:</p>
              <div className="flex" style={{ flexDirection: "column", gap: 8 }}>
                <button type="button" className="btn btn-gold" data-testid="service-edit-scope-occurrence" onClick={() => applyEditScope("occurrence")}>This occurrence only</button>
                <button type="button" className="btn btn-outline" data-testid="service-edit-scope-future" onClick={() => applyEditScope("future")}>All future occurrences</button>
                <button type="button" className="btn btn-outline" onClick={() => setEditScopeAsk(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ----- Reusable color picker (8 presets + custom hex) ----------------------
export const COLOR_PRESETS = [
  { label: "Royal Gold",   value: "var(--kc-accent)" },
  { label: "Sky Blue",     value: "#5FB1FF" },
  { label: "Coral Red",    value: "#FF6B6B" },
  { label: "Forest Green", value: "#34A853" },
  { label: "Purple",       value: "#A78BFA" },
  { label: "Orange",       value: "#FF8A65" },
  { label: "Teal",         value: "#26A69A" },
  { label: "Pink",         value: "#FF8FB6" },
];

export function ColorPickerField({ label = "Color", testId = "color-picker", value, onChange }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }} data-testid={testId}>
        {COLOR_PRESETS.map(c => (
          <button
            key={c.value}
            type="button"
            title={c.label}
            data-testid={`${testId}-preset-${c.value.replace("#", "")}`}
            onClick={() => onChange(c.value)}
            style={{
              width: 22, height: 22, borderRadius: "50%",
              background: c.value,
              border: value && value.toLowerCase() === c.value.toLowerCase() ? "2.5px solid #FFFFFF" : "1.5px solid rgba(255,255,255,0.18)",
              boxShadow: value && value.toLowerCase() === c.value.toLowerCase() ? "0 0 0 1px " + c.value : "none",
              cursor: "pointer", padding: 0,
            }}
          />
        ))}
        <input
          type="text"
          placeholder="#hex"
          maxLength={7}
          value={value || ""}
          data-testid={`${testId}-hex`}
          onChange={e => onChange(e.target.value)}
          style={{ width: 80, padding: "4px 8px", fontSize: 12, borderRadius: 6, border: "1px solid rgba(255,255,255,0.20)", background: "var(--kc-surface)", color: "#fff" }}
        />
      </div>
    </div>
  );
}
